import type { Component } from 'vue';
import type { ConnectAppModule } from '@blpde/connect';
import {
  BarChart3,
  BookText,
  Bot,
  Building2,
  Calculator,
  CalendarDays,
  CalendarPlus,
  Cloud,
  FileScan,
  ShieldCheck,
  FilePen,
  FileText,
  Database,
  HardDrive,
  MessagesSquare,
  Inbox,
  Mail,
  MapPin,
  MessageCircle,
  PenLine,
  Plus,
  Settings2,
  Upload,
  Users,
  UsersRound,
  Shield,
  SpellCheck,
  AppWindow,
  Workflow,
  Cpu,
  Blocks,
  Network,
  MonitorSmartphone,
  NotebookPen,
  KeyRound,
  Repeat,
  Waypoints,
} from '@lucide/vue';
import { BoardIcon, DatabaseMultipleIcon } from '@blpde/nx-icons';
import { erpCommands } from './erp';
import { mailCommands } from './mail';
import { calendarCommands } from './calendar';
import { designerNav, designerCommands } from './designer';
import { agentCommands } from './agent';
import { adminCommands } from './admin';
import { calcCommands } from './calc';
import { chatCommands } from './chat';
import { dashboardCommands } from './dashboard';
import { flowCommands } from './flow';
import { sitesCommands } from './sites';
import { notesCommands } from './notes';
import { wikiCommands } from './wiki';
import { writeCommands } from './write';
import type { NavSpec } from './nav';
import type { CommandFactory } from './commands';

export type AppId =
  | 'erp'
  | 'designer'
  | 'mail'
  | 'calendar'
  | 'calc'
  | 'drive'
  | 'agent'
  | 'flow'
  | 'sites'
  | 'chat'
  | 'wiki'
  | 'notes'
  | 'write'
  | 'dashboard'
  | 'admin';

export interface AppEntry {
  id: AppId;
  /** i18n key for tooltip + home tile + app-switcher label */
  labelKey: string;
  /** route prefix, e.g. '/erp' */
  basePath: string;
  /** lucide icon component (app-switcher + home tile) */
  icon: Component;
  /**
   * Loads the app's module (`@<app>/connect`) — its own bundle in the production
   * build. The shell calls it the first time the app is opened and mounts the
   * module's route (see ./loader.ts).
   */
  load: () => Promise<{ default: ConnectAppModule }>;
  /**
   * `load` calls the API (the ERP loads its screen definitions), so the app is only
   * loaded for a signed-in user. Without a valid session the navigation is not
   * loaded but handed to the auth gate, which sends it to the login.
   */
  loadNeedsSession?: boolean;
  /**
   * Master module key required for this app to work — checked against
   * `/v1/modules` `connectedClients`.
   */
  moduleKey: string;
  /**
   * Formats of Drive's catalog this app opens. The module key cannot say it:
   * several apps run on `connect`.
   */
  formatKeys?: readonly string[];
  /**
   * Backend modules without an app of their own whose pages and settings this
   * app hosts: it may declare settings on their keys (the admin app: `metrics`).
   */
  hostedModuleKeys?: readonly string[];
  /** Optional effective permission(s) required to expose the app in shell UI. */
  requiredPermissions?: string | string[];
  permissionMode?: 'all' | 'any';
  /**
   * Navbar contribution rendered by the shell when this app is active. A loaded
   * app module's own `nav` replaces it. Item texts are keys of the app's own
   * table (I-2284) or plain labels.
   */
  nav?: NavSpec;
  /**
   * Command-palette contribution. The shell auto-generates an "open <app>"
   * command for every entry; set this to inject deeper jumps (e.g. open a
   * specific surface). Receives a {@link ModuleCommandContext}: router, `t` over
   * the app's own table and the app's name.
   */
  commands?: CommandFactory;
  /**
   * Extensions the app brings to every rich-text editor (I-2799): a small
   * public module of its own (`@<app>/connect/editorExtensions`), loaded by
   * the shell at startup and offered while the app is usable
   * (`editorExtensions.ts`).
   */
  editorExtensions?: () => Promise<{ editorExtensions: import('@/editorExtensions').EditorExtensionFactory }>;
  /**
   * The app contributes tabs to the component showcase (`/showcase`): the page
   * loads the app's module and shows its `showcase` tabs (I-2032).
   */
  showcase?: boolean;
  /**
   * The app contributes to the settings: the hub (`/settings`, I-2020) loads the
   * app's module and lists its `settings` topics under the app's name, the one
   * settings list (I-2510) shows its `settingsSections`.
   */
  settings?: boolean;
  /**
   * The app offers further layers to the settings list's „Bearbeiten für"
   * (`settingsScopes` of its module, I-2510) — the admin app: the session's
   * company and tenant.
   */
  settingsScopes?: boolean;
}

export const apps: AppEntry[] = [
  {
    id: 'erp',
    labelKey: 'connect.apps.erp',
    basePath: '/erp',
    icon: Building2,
    loadNeedsSession: true,
    load: async () => {
      // The ERP's screen definitions come over the API (BLP/nx#3257); its module graph
      // evaluates with them in place. A failed load rejects, so the loader keeps its
      // placeholder and tries again on the next navigation.
      const { loadErpDefinitions } = await import('@erp/connect/shell');
      await loadErpDefinitions();
      return import('@erp/connect');
    },
    moduleKey: 'erp',
    commands: erpCommands,
    editorExtensions: () => import('@erp/connect/editorExtensions'),
    showcase: true,
    settings: true,
  },
  {
    id: 'designer',
    labelKey: 'connect.apps.designer',
    basePath: '/designer',
    icon: FileText,
    load: () => import('@designer/connect'),
    // Designer talks to the `reporting` module (BLP-Module: reporting header in
    // its API client) — gate availability on that, NOT erp. reporting fans out
    // to erp for render data, but reachability of reporting is the real signal.
    moduleKey: 'reporting',
    requiredPermissions: 'reports.view',
    nav: designerNav,
    commands: designerCommands,
  },
  {
    id: 'mail',
    labelKey: 'connect.apps.mail',
    basePath: '/mail',
    icon: Mail,
    load: () => import('@mail/connect'),
    moduleKey: 'mail',
    settings: true,
    commands: mailCommands,
    nav: {
      left: [
        { id: 'mail.inbox', text: 'Posteingang', icon: Inbox, to: '/mail' },
        { id: 'mail.compose', text: 'mail.command.compose', icon: PenLine, to: '/mail?compose=1' },
      ],
    },
  },
  {
    id: 'calendar',
    labelKey: 'connect.apps.calendar',
    basePath: '/calendar',
    icon: CalendarDays,
    load: () => import('@calendar/connect'),
    moduleKey: 'calendar',
    settings: true,
    commands: calendarCommands,
    nav: {
      left: [
        { id: 'calendar.today', text: 'Heute', icon: CalendarDays, to: '/calendar' },
        {
          id: 'calendar.new',
          text: 'calendar.shortcuts.newEvent',
          icon: CalendarPlus,
          to: '/calendar?new=1',
        },
      ],
    },
  },
  {
    id: 'calc',
    labelKey: 'connect.apps.calc',
    basePath: '/calc',
    icon: Calculator,
    load: () => import('@calc/connect'),
    moduleKey: 'connect',
    formatKeys: ['calcz', 'xlsx'],
    commands: calcCommands,
    settings: true,
    nav: {
      left: [{ id: 'calc.workbooks', text: 'Tabellen', icon: Calculator, to: '/calc' }],
    },
  },
  {
    id: 'write',
    labelKey: 'connect.apps.write',
    basePath: '/write',
    icon: FilePen,
    load: () => import('@write/connect'),
    moduleKey: 'connect',
    formatKeys: ['writez', 'docx'],
    commands: writeCommands,
    settings: true,
    nav: {
      left: [{ id: 'write.documents', text: 'Dokumente', icon: FilePen, to: '/write' }],
    },
  },
  {
    id: 'drive',
    showcase: true,
    labelKey: 'connect.apps.drive',
    basePath: '/drive',
    icon: Cloud,
    load: () => import('@drive/connect'),
    moduleKey: 'drive',
    settings: true,
    nav: {
      left: [
        { id: 'drive.browse', text: 'Dateien', icon: HardDrive, to: '/drive' },
        { id: 'drive.upload', text: 'Upload', icon: Upload, to: '/drive' },
      ],
    },
  },
  {
    id: 'agent',
    labelKey: 'connect.apps.agent',
    basePath: '/agent',
    icon: Bot,
    load: () => import('@agent/connect'),
    moduleKey: 'agent',
    commands: agentCommands,
    settings: true,
    nav: {
      left: [
        { id: 'agent.chat', text: 'Chats', icon: Bot, to: '/agent' },
        { id: 'agent.new', text: 'Neuer Chat', icon: Plus, to: '/agent' },
      ],
    },
  },
  {
    id: 'flow',
    labelKey: 'connect.apps.flow',
    basePath: '/flow',
    icon: Workflow,
    load: () => import('@flow/connect'),
    moduleKey: 'flow',
    commands: flowCommands,
    nav: {
      left: [
        { id: 'flow.list', text: 'Automationen', icon: Workflow, to: '/flow' },
        { id: 'flow.new', text: 'Neue Automation', icon: Plus, to: '/flow/new' },
      ],
    },
  },
  {
    id: 'sites',
    labelKey: 'connect.apps.sites',
    basePath: '/sites',
    icon: MapPin,
    load: () => import('@sites/connect'),
    moduleKey: 'sites',
    commands: sitesCommands,
    nav: {
      left: [{ id: 'sites.sites', text: 'sites.nav.sites', icon: MapPin, to: '/sites' }],
    },
  },
  {
    id: 'chat',
    labelKey: 'connect.apps.chat',
    basePath: '/chat',
    icon: MessageCircle,
    load: () => import('@chat/connect'),
    moduleKey: 'chat',
    commands: chatCommands,
    settings: true,
    nav: {
      left: [{ id: 'chat.messages', text: 'chat.nav.messages', icon: MessagesSquare, to: '/chat' }],
    },
  },
  {
    id: 'wiki',
    labelKey: 'connect.apps.wiki',
    basePath: '/wiki',
    icon: BookText,
    load: () => import('@wiki/connect'),
    moduleKey: 'wiki',
    commands: wikiCommands,
    nav: {
      left: [{ id: 'wiki.pages', text: 'Seiten', icon: BookText, to: '/wiki' }],
    },
  },
  {
    id: 'notes',
    labelKey: 'connect.apps.notes',
    basePath: '/notes',
    icon: NotebookPen,
    load: () => import('@notes/connect'),
    moduleKey: 'connect',
    formatKeys: ['note'],
    commands: notesCommands,
    settings: true,
    nav: {
      left: [{ id: 'notes.notes', text: 'Notizen', icon: NotebookPen, to: '/notes' }],
    },
  },
  {
    id: 'dashboard',
    labelKey: 'connect.apps.dashboard',
    basePath: '/dashboard',
    // Same identity as the ERP viewer entry (erp.ts nav): BoardIcon + "Dashboard".
    icon: BoardIcon,
    load: () => import('@dashboard/connect'),
    moduleKey: 'dashboard',
    commands: dashboardCommands,
    settings: true,
    nav: {
      left: [
        {
          id: 'dashboard.list',
          text: 'Dashboard',
          icon: BoardIcon,
          to: '/dashboard',
        },
      ],
    },
  },
  {
    id: 'admin',
    labelKey: 'connect.apps.admin',
    basePath: '/admin',
    icon: Settings2,
    load: () => import('@admin/connect'),
    // Master-owned surfaces — always available when the shell is up.
    moduleKey: 'connect',
    // The Metrics and Storage pages live here; those modules have no app of their own.
    hostedModuleKeys: ['metrics', 'storage', 'antivirus'],
    // The session's company and tenant in the settings list, for administrators.
    settingsScopes: true,
    // Its own settings (the database browser, I-2854) in the one settings list.
    settings: true,
    commands: adminCommands,
    nav: {
      left: [
        {
          id: 'admin.dashboard',
          text: 'admin.nav.dashboard',
          icon: Settings2,
          to: '/admin',
        },
        {
          id: 'admin.general',
          text: 'admin.nav.general',
          icon: Settings2,
          children: [
            {
              id: 'admin.modules',
              text: 'admin.nav.modules',
              icon: HardDrive,
              to: '/admin/modules',
            },
            {
              id: 'admin.accounts',
              text: 'admin.nav.accounts',
              icon: Users,
              to: '/admin/accounts',
            },
            {
              id: 'admin.applications',
              text: 'admin.nav.applications',
              icon: AppWindow,
              to: '/admin/applications',
            },
            {
              id: 'admin.tenants',
              text: 'admin.nav.tenants',
              icon: Network,
              to: '/admin/tenants',
            },
            {
              id: 'admin.companies',
              text: 'admin.nav.companies',
              icon: Building2,
              to: '/admin/companies',
            },
            {
              id: 'admin.groups',
              text: 'admin.nav.groups',
              icon: UsersRound,
              to: '/admin/groups',
            },
            {
              id: 'admin.permissions',
              text: 'admin.nav.permissions',
              icon: Shield,
              to: '/admin/permissions',
            },
            {
              id: 'admin.companyDictionary',
              text: 'admin.nav.companyDictionary',
              icon: SpellCheck,
              to: '/admin/company-dictionary',
            },
            {
              id: 'admin.tenantDictionary',
              text: 'admin.nav.tenantDictionary',
              icon: SpellCheck,
              to: '/admin/tenant-dictionary',
            },
          ],
        },
        {
          id: 'admin.chat',
          text: 'admin.nav.chat',
          icon: MessagesSquare,
          to: '/admin/chat',
        },
        {
          id: 'admin.agent',
          text: 'admin.nav.agent',
          icon: Bot,
          children: [
            {
              id: 'admin.agents',
              text: 'admin.nav.agents',
              icon: Bot,
              to: '/admin/agents',
            },
            {
              id: 'admin.providers',
              text: 'admin.nav.providers',
              icon: Cpu,
              to: '/admin/providers',
            },
          ],
        },
        {
          id: 'admin.drive',
          text: 'admin.nav.drive',
          icon: HardDrive,
          children: [
            {
              id: 'admin.drive.mounts',
              text: 'admin.drive.nav.mounts',
              icon: HardDrive,
              to: '/admin/drive',
            },
            {
              id: 'admin.drive.storageConnections',
              text: 'admin.drive.nav.storageConnections',
              icon: Database,
              to: '/admin/drive/storage-connections',
            },
            {
              id: 'admin.drive.shares',
              text: 'admin.drive.nav.shares',
              icon: Cloud,
              to: '/admin/drive/shares',
            },
            {
              id: 'admin.drive.audit',
              text: 'admin.drive.nav.audit',
              icon: FileText,
              to: '/admin/drive/audit',
            },
            {
              id: 'admin.drive.index',
              text: 'admin.drive.nav.index',
              icon: Blocks,
              to: '/admin/drive/index',
            },
          ],
        },
        {
          id: 'admin.antivirus',
          text: 'admin.nav.antivirus',
          icon: ShieldCheck,
          to: '/admin/antivirus',
        },
        {
          id: 'admin.ocr',
          text: 'admin.nav.ocr',
          icon: FileScan,
          to: '/admin/ocr',
        },
        {
          id: 'admin.mail',
          text: 'admin.nav.mail',
          icon: Mail,
          children: [
            {
              id: 'admin.mail.overview',
              text: 'admin.mail.nav.overview',
              icon: Mail,
              to: '/admin/mail',
            },
            {
              id: 'admin.mail.mailboxes',
              text: 'admin.mail.nav.mailboxes',
              icon: Inbox,
              to: '/admin/mail/mailboxes',
            },
            {
              id: 'admin.mail.domains',
              text: 'admin.mail.nav.domains',
              icon: Network,
              to: '/admin/mail/domains',
            },
            {
              id: 'admin.mail.queues',
              text: 'admin.mail.nav.queues',
              icon: Repeat,
              to: '/admin/mail/queues',
            },
            {
              id: 'admin.mail.audit',
              text: 'admin.mail.nav.audit',
              icon: FileText,
              to: '/admin/mail/audit',
            },
          ],
        },
        {
          id: 'admin.calendar',
          text: 'admin.nav.calendar',
          icon: CalendarDays,
          children: [
            {
              id: 'admin.calendar.overview',
              text: 'admin.calendar.nav.overview',
              icon: CalendarDays,
              to: '/admin/calendar',
            },
            {
              id: 'admin.calendar.audit',
              text: 'admin.calendar.nav.audit',
              icon: FileText,
              to: '/admin/calendar/audit',
            },
          ],
        },
        {
          id: 'admin.storage',
          text: 'admin.nav.storage',
          icon: Database,
          children: [
            {
              id: 'admin.storage.overview',
              text: 'admin.storage.nav.overview',
              icon: Database,
              to: '/admin/storage',
            },
            {
              id: 'admin.storage.buckets',
              text: 'admin.storage.nav.buckets',
              icon: Blocks,
              to: '/admin/storage/buckets',
            },
            {
              id: 'admin.storage.objects',
              text: 'admin.storage.nav.objects',
              icon: FileText,
              to: '/admin/storage/objects',
            },
          ],
        },
        {
          id: 'admin.edge',
          text: 'admin.nav.edge',
          icon: Waypoints,
          children: [
            {
              id: 'admin.edge.overview',
              text: 'admin.edge.nav.overview',
              icon: Waypoints,
              to: '/admin/edge',
            },
            {
              id: 'admin.edge.names',
              text: 'admin.edge.nav.names',
              icon: Network,
              to: '/admin/edge/names',
            },
            {
              id: 'admin.edge.federation',
              text: 'admin.edge.nav.federation',
              icon: Repeat,
              to: '/admin/edge/federation',
            },
            {
              id: 'admin.edge.access',
              text: 'admin.edge.nav.access',
              icon: Shield,
              to: '/admin/edge/access',
            },
          ],
        },
        {
          id: 'admin.system',
          text: 'admin.nav.system',
          icon: Settings2,
          children: [
            {
              id: 'admin.mcpGroups',
              text: 'admin.nav.mcpGroups',
              icon: Blocks,
              to: '/admin/mcp-groups',
            },
            {
              id: 'admin.sessions',
              text: 'admin.nav.sessions',
              icon: MonitorSmartphone,
              to: '/admin/sessions',
            },
            {
              id: 'admin.oauth',
              text: 'admin.nav.oauth',
              icon: KeyRound,
              to: '/admin/oauth',
            },
            {
              id: 'admin.database',
              text: 'admin.nav.database',
              icon: DatabaseMultipleIcon,
              to: '/admin/database',
            },
            {
              id: 'admin.metrics',
              text: 'admin.nav.metrics',
              icon: BarChart3,
              to: '/admin/metrics',
            },
          ],
        },
      ],
    },
  },
];

export function appAllowed(
  app: AppEntry,
  can: (requirement: string | string[], mode?: 'all' | 'any') => boolean,
): boolean {
  if (!app.requiredPermissions) return true;
  return can(app.requiredPermissions, app.permissionMode ?? 'all');
}

/** Resolve the active app entry from a route path (e.g. `/erp/sales/quotes`). */
export function appForPath(path: string): AppEntry | undefined {
  // A tile's route may carry a query or a hash.
  const segment = path.split(/[/?#]/)[1];
  return apps.find((a) => a.id === segment);
}
